/**
 * Marketing site content — ported verbatim from the design handoff
 * (`site/content.jsx`, `window.FDM`). Single source of truth for the landing
 * page sections. Copy is sourced from the FRAUDefender brochures/decks.
 *
 * Field names match the design exactly so the section components map 1:1 to the
 * prototype JSX.
 */

export type Tone = 'blue' | 'cyan' | 'teal' | 'violet' | 'green' | 'amber' | 'rose';

export interface LifecycleStage {
  key: string;
  label: string;
  tone: Tone;
  icon: string;
  kicker: string;
  title: string;
  blurb: string;
  points: string[];
  stat: { fig: string; label: string };
  isNew?: boolean;
}

export interface Product {
  id: string;
  name: string;
  alias: string;
  tone: Tone;
  icon: string;
  stage: string;
  tagline: string;
  desc: string;
  meta: string[];
}

export interface Agent {
  id: string;
  name: string;
  tone: Tone;
  icon: string;
  domain: string;
  blurb: string;
}

export interface QueueItem {
  ref: string;
  type: string;
  merchant: string;
  mid: string;
  amount: string;
  agent: string;
  tone: Tone;
  conf: number;
  threshold: number;
  status: 'auto' | 'hitl';
  action: string;
  reason: string;
}

export interface Stat {
  fig: number;
  suffix: string;
  decimals: number;
  label: string;
  sub: string;
  tone: Tone;
  icon: string;
}

export interface Faq {
  q: string;
  a: string;
}

export interface Audience {
  id: string;
  icon: string;
  tone: Tone;
  name: string;
  blurb: string;
  points: string[];
}

export interface NavLink {
  label: string;
  href: string;
}

export const lifecycle: LifecycleStage[] = [
  {
    key: 'prevent',
    label: 'Prevent',
    tone: 'cyan',
    icon: 'eye',
    kicker: 'Stage 01 · before a dispute exists',
    title: 'Stop disputes at the cardholder inquiry',
    blurb:
      'The moment a cardholder questions a charge with their bank, PREVENT pushes a full Enhanced Digital Receipt to the issuer’s agent — in real time, before any dispute is filed.',
    points: [
      'Order Insight delivers transaction detail to the issuer in ~2.5 seconds',
      'PREVENT+ (CE3) blocks friendly fraud using prior-transaction history',
      'Many inquiries resolve with no refund and no chargeback',
    ],
    stat: { fig: '15–45%', label: 'of pre-disputes deflected at inquiry' },
  },
  {
    key: 'resolve',
    label: 'Resolve',
    tone: 'blue',
    icon: 'refresh-cw',
    kicker: 'Stage 02 · before a chargeback is filed',
    title: 'Cancel disputes before they become chargebacks',
    blurb:
      'If a dispute is opened, RESOLVE intervenes automatically — issuing a credit through RDR, CDRN or MC Resolve so the dispute is cancelled before it ever hardens into a chargeback.',
    points: [
      'RDR auto-resolves eligible Visa disputes by your own rules',
      'CDRN gives card-brand-agnostic coverage with a 72-hour window',
      'MC Resolve extends the same protection across Mastercard',
    ],
    stat: { fig: '99%+', label: 'of eligible disputes resolved automatically' },
  },
  {
    key: 'inform',
    label: 'Inform',
    tone: 'violet',
    icon: 'radar',
    kicker: 'Stage 03 · total fraud visibility',
    title: 'See every confirmed fraud and dispute, in real time',
    blurb:
      'INFORM centralizes Visa-confirmed fraud notifications (the basis of the TC-40 report) and full dispute alerts through a single integration — so you can track trends and act before losses compound.',
    points: [
      'All Visa-confirmed fraud (TC-40) and dispute alerts, one feed',
      'Real-time delivery via API the instant a card network reports',
      'Refine detection, spot operational issues, protect revenue',
    ],
    stat: { fig: '99%+', label: 'of global issuers in the network' },
  },
  {
    key: 'recover',
    label: 'Recover',
    tone: 'green',
    icon: 'file-signature',
    kicker: 'Stage 04 · the agentic representment engine',
    title: 'Win back revenue with agent-built representments',
    blurb:
      'When a chargeback does land, the Representment Engine drafts the narrative, assembles compelling evidence, runs every eligibility gate, and files the clean cases automatically — escalating only what needs a human.',
    points: [
      'Representment Drafter writes the dispute narrative + links evidence',
      'Auto-submit gate files clear cases; the rest route to a review queue',
      'Every action carries a confidence score and a full audit trail',
    ],
    stat: { fig: '4 agents', label: 'working each case end-to-end' },
    isNew: true,
  },
];

export const products: Product[] = [
  {
    id: 'prevent',
    name: 'PREVENT',
    alias: 'Order Insight',
    tone: 'cyan',
    icon: 'eye',
    stage: 'Prevent',
    tagline: 'Real-time transaction verification at the issuer.',
    desc: 'Connected directly to the card network: when a cardholder calls their bank, we assemble an Enhanced Digital Receipt — date, time, location, URL, product, delivery — and put it in front of the issuer’s agent within ~2.5 seconds.',
    meta: ['No refund required', '~2.5s receipt', 'Visa network'],
  },
  {
    id: 'preventplus',
    name: 'PREVENT+',
    alias: 'CE3 / Compelling Evidence 3.0',
    tone: 'cyan',
    icon: 'shield-check',
    stage: 'Prevent',
    tagline: 'Advanced pre-dispute prevention from purchase history.',
    desc: 'Qualifies a disputed transaction against the cardholder’s own history — two prior undisputed transactions from the same PAN within 120–365 days. When the pattern holds, the dispute is blocked as friendly fraud and we submit the evidence.',
    meta: ['Friendly-fraud block', 'History-qualified', 'No refund'],
  },
  {
    id: 'rdr',
    name: 'RDR',
    alias: 'Rapid Dispute Resolution',
    tone: 'blue',
    icon: 'zap',
    stage: 'Resolve',
    tagline: 'Preemptive, rule-based chargeback prevention for Visa.',
    desc: 'Resolves Visa disputes automatically before they become formal chargebacks. You define the resolution rules; RDR executes in real time with no manual intervention — capable of resolving 99%+ of eligible disputes.',
    meta: ['99%+ resolved', 'Your rules', 'Flat fee'],
  },
  {
    id: 'cdrn',
    name: 'CDRN',
    alias: 'Cardholder Dispute Resolution Network',
    tone: 'blue',
    icon: 'network',
    stage: 'Resolve',
    tagline: 'Card-brand-agnostic dispute protection.',
    desc: 'Unlike RDR and MC Resolve, CDRN spans most major card brands — a universal safety net. When an alert arrives, we refund on your behalf or pass it to you with a 72-hour window, stopping escalation.',
    meta: ['Multi-brand', '72-hour window', 'Pre-VAMP'],
  },
  {
    id: 'mcresolve',
    name: 'MC Resolve',
    alias: 'MC Resolve + MC Insight',
    tone: 'blue',
    icon: 'refresh-cw',
    stage: 'Resolve',
    tagline: 'Mastercard dispute resolution & transaction insight.',
    desc: 'MC Resolve automatically refunds on alert to prevent a chargeback; MC Insight delivers real-time transaction transparency to Mastercard cardholders — reducing chargebacks without issuing refunds.',
    meta: ['Mastercard', 'Auto-refund', 'Insight feed'],
  },
  {
    id: 'inform',
    name: 'INFORM',
    alias: 'Fraud Intelligence',
    tone: 'violet',
    icon: 'radar',
    stage: 'Inform',
    tagline: 'Centralized, real-time fraud & dispute intelligence.',
    desc: 'One integration delivers all Visa-confirmed fraud notifications (TC-40) plus comprehensive fraud and non-fraud dispute alerts — the intelligence layer to monitor trends, refine detection and protect revenue streams.',
    meta: ['TC-40 feed', 'One API', 'Trend analytics'],
  },
];

export const agents: Agent[] = [
  {
    id: 'refundgate',
    name: 'RefundGate',
    tone: 'blue',
    icon: 'gauge',
    domain: 'Alerts',
    blurb:
      'Recommends refund, decline or escalate on every inbound alert — with a confidence score and the rules it cleared.',
  },
  {
    id: 'drafter',
    name: 'Representment Drafter',
    tone: 'violet',
    icon: 'file-signature',
    domain: 'Chargebacks',
    blurb: 'Writes the dispute narrative tailored to the reason code and links the supporting evidence.',
  },
  {
    id: 'collector',
    name: 'Evidence Collector',
    tone: 'violet',
    icon: 'layers',
    domain: 'Chargebacks',
    blurb: 'Assembles the compelling-evidence package — receipts, auth signals, delivery proof, prior history.',
  },
  {
    id: 'cepipe',
    name: 'CE Pipeline',
    tone: 'cyan',
    icon: 'workflow',
    domain: 'Compelling Evidence',
    blurb: 'Deterministic eligibility checks paired with AI evidence selection, so only qualified cases proceed.',
  },
];

export const queue: QueueItem[] = [
  {
    ref: 'CB-2024-31188',
    type: 'Chargeback',
    merchant: 'Bytehaus',
    mid: 'MID-2204',
    amount: '$1,204.00',
    agent: 'Representment Drafter',
    tone: 'violet',
    conf: 0.86,
    threshold: 0.7,
    status: 'auto',
    action: 'Representment filed',
    reason:
      'Compelling evidence — device + IP match 3 prior undisputed transactions; AVS + CVV passed.',
  },
  {
    ref: 'ETH-55120934',
    type: 'Alert',
    merchant: 'Coastline Travel',
    mid: 'MID-9015',
    amount: '$1,842.00',
    agent: 'RefundGate',
    tone: 'blue',
    conf: 0.58,
    threshold: 0.75,
    status: 'hitl',
    action: 'Escalated to staff',
    reason: 'Partial-service claim, amount over auto cap, reason 4853 on always-review list.',
  },
  {
    ref: 'CE-7740',
    type: 'Compelling Evidence',
    merchant: 'Apex Fitness',
    mid: 'MID-7741',
    amount: '$89.00',
    agent: 'CE Pipeline',
    tone: 'cyan',
    conf: 0.91,
    threshold: 0.7,
    status: 'auto',
    action: 'CE package submitted',
    reason: 'Recurring-cancellation pattern; signed terms + cancellation timestamp on file.',
  },
  {
    ref: 'ALT-44120',
    type: 'Alert',
    merchant: 'Northwind Supply',
    mid: 'MID-3019',
    amount: '$249.00',
    agent: 'RefundGate',
    tone: 'blue',
    conf: 0.91,
    threshold: 0.75,
    status: 'auto',
    action: 'Refund issued',
    reason:
      'Non-delivery; parcel returned to sender 9 days ago, never re-shipped. Refund cheaper than loss.',
  },
];

export const stats: Stat[] = [
  { fig: 99, suffix: '%+', decimals: 0, label: 'of eligible disputes resolved automatically', sub: 'via Rapid Dispute Resolution', tone: 'blue', icon: 'zap' },
  { fig: 2.5, suffix: 's', decimals: 1, label: 'to deliver an Enhanced Digital Receipt', sub: 'to the issuer’s agent, live on the call', tone: 'cyan', icon: 'clock' },
  { fig: 99, suffix: '%+', decimals: 0, label: 'of global issuers in the network', sub: 'one integration, worldwide reach', tone: 'violet', icon: 'globe' },
  { fig: 4, suffix: '', decimals: 0, label: 'lifecycle stages, one platform', sub: 'Prevent · Resolve · Inform · Recover', tone: 'green', icon: 'layers' },
];

export const vampThreshold = { now: 0.9, soon: 0.3 };

export const faqs: Faq[] = [
  {
    q: 'How is the agentic Representment Engine different from a rules engine?',
    a: 'Rules still gate everything — but agents do the work between the rules. The Drafter writes a reason-code-specific narrative, the Collector assembles the evidence, and each action lands with a confidence score and audit trail. The auto-submit gate files only the cases that clear every check; everything else routes to a human queue.',
  },
  {
    q: 'Do refund-issuing actions ever fire without a human?',
    a: 'Refund and credit-issuing flows never auto-enable. Agents can recommend and draft, and you can opt specific low-risk, capped flows into automation — but the platform is built so money-moving actions require your sign-off by default.',
  },
  {
    q: 'How does this relate to Visa’s VAMP changes?',
    a: 'From April 2025, TC-40 fraud alerts resolved through RDR count toward your VAMP ratio, and the threshold tightens toward 0.3% by 2026. The lifecycle is designed to attack the ratio upstream — preventing and resolving before alerts are ever filed, while INFORM gives you the daily visibility to stay ahead.',
  },
  {
    q: 'Each service is separate — can we take only what we need?',
    a: 'Yes. Every service is distinct and you choose which to enable. They’re designed to work sequentially, hand in hand, so running them together gives the most complete coverage at the lowest mitigation cost — but the choice is yours.',
  },
  {
    q: 'What does integration look like for a processor or PSP?',
    a: 'A single integration links you to the global issuer network across Prevent, Resolve, Inform and Recover. Connect directly to Order Insight or work through us as a reseller; enroll merchants and configure per-MID rules, caps and auto-submit behavior from the console.',
  },
];

export const audiences: Audience[] = [
  {
    id: 'processors',
    icon: 'network',
    tone: 'blue',
    name: 'Processors, PSPs & CMS partners',
    blurb:
      'One integration to the global issuer network. Offer the full dispute lifecycle to your merchant base as a value-add, with per-MID configuration and reseller-grade controls.',
    points: [
      'Multi-tenant, per-MID rules & caps',
      'Reseller integration to Order Insight',
      'Portfolio-level fraud & dispute analytics',
    ],
  },
  {
    id: 'merchants',
    icon: 'building-2',
    tone: 'cyan',
    name: 'Merchants under VAMP pressure',
    blurb:
      'Attack your VAMP ratio upstream. Prevent and resolve disputes before they’re filed, recover the ones worth fighting, and watch fraud trends daily — so you stay well under acquirer thresholds.',
    points: [
      'Daily VAMP-ratio visibility',
      'Friendly-fraud blocking with CE3',
      'Agentic representments to recover revenue',
    ],
  },
];

export const nav: NavLink[] = [
  { label: 'Lifecycle', href: '#lifecycle' },
  { label: 'Products', href: '#products' },
  { label: 'Representment Engine', href: '#engine' },
  { label: 'VAMP', href: '#vamp' },
  { label: 'Pricing model', href: '#pricing' },
];

/**
 * Route helper — the landing is single-page, so product/section targets are
 * in-page anchors. Login/enrollment point at the real app routes.
 */
export function href(target: string): string {
  const map: Record<string, string> = {
    lifecycle: '#lifecycle',
    products: '#products',
    engine: '#engine',
    vamp: '#vamp',
    demo: '#engine',
    pricing: '#pricing',
    enroll: '/enroll',
    login: '/login',
    home: '/',
  };
  return map[target] ?? '#';
}
